<script setup lang="ts">
import type {
  SiteSectionsTreeSectionCategoryFilterOption,
  SiteSectionsTreeSectionCategoryFilterSelectionMode,
  SiteSectionsTreeSectionCategoryFilterVariant,
} from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { SiteSectionCategoryId } from "@/data/common/ids";

import { cn, normalizeForSearch } from "@voltalis/frontend-utils";
import { last } from "es-toolkit";
import { computed, ref, useAttrs, useTemplateRef } from "vue";

import {
  createMultiSelectPassThroughOptions,
  createSingleSelectPassThroughOptions,
} from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/use-site-sections-tree-section-category-filter-pass-through-options";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import MultiSelect from "primevue/multiselect";
import Select from "primevue/select";

import SiteSectionsTreeSectionCategoryFilterFooter from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter-footer.vue";
import SiteSectionsTreeSectionCategoryFilterOptionItem from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter-option.vue";
import SiteSectionsTreeSectionCategoryFilterValue from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter-value.vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  options: readonly SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory?: (name: string) => Promise<boolean>;
  clearCategory?: () => Promise<boolean> | boolean;
  clearCategoryLabel?: string;
  label?: string;
  selectionMode?: SiteSectionsTreeSectionCategoryFilterSelectionMode;
  showCreateCategory?: boolean;
  variant?: SiteSectionsTreeSectionCategoryFilterVariant;
  isCreatingCategory?: boolean;
}>();

const emits = defineEmits<{
  (e: "hide"): void;
}>();

defineSlots<{
  footer(props: { hideSelectOptions: () => void }): unknown;
}>();

const selectedCategories = defineModel<SiteSectionCategoryId[]>("selectedCategories", { required: true });

const { t, k } = useTypedIntl();
const attrs = useAttrs();

const isCategoryCreationFormVisible = ref<boolean>(false);
const singleSelect = useTemplateRef("singleSelect");
const multiSelect = useTemplateRef("multiSelect");

const selectedCategoryCount = computed<number>(() => {
  return props.options.filter((option) => selectedCategories.value.includes(option.id)).length;
});
const hasSelectedCategories = computed<boolean>(() => selectedCategoryCount.value > 0);
const selectedCategory = computed<SiteSectionCategoryId | undefined>({
  get: () => last(selectedCategories.value),
  set: (categoryId) => {
    selectedCategories.value = categoryId === undefined ? [] : [categoryId];
  },
});
const defaultLabel = computed<string>(() => props.label ?? t(k.tools.siteManagement.siteTreeStructure.categoryFilter));
const selectedCategoryLabel = computed<string | undefined>(() => {
  return props.options.find((option) => option.id === selectedCategory.value)?.label;
});
const singleSelectValueLabel = computed<string>(() => selectedCategoryLabel.value ?? defaultLabel.value);
const canCreateCategory = computed<boolean>(() => props.showCreateCategory && !!props.createCategory);
const filterVariant = computed<SiteSectionsTreeSectionCategoryFilterVariant>(() => props.variant ?? "default");
const multiSelectOptions = computed<SiteSectionsTreeSectionCategoryFilterOption[]>(() => [...props.options]);
const multiSelectPassThroughOptions = computed(() =>
  createMultiSelectPassThroughOptions(isCategoryCreationFormVisible.value),
);
const selectPassThroughOptions = computed(() => createSingleSelectPassThroughOptions(filterVariant.value));

function startCategoryCreation() {
  isCategoryCreationFormVisible.value = true;
}

function resetCategoryCreationForm() {
  isCategoryCreationFormVisible.value = false;
}

async function handleClearCategory() {
  await props.clearCategory?.();
}

async function confirmCategoryCreation(categoryName: string) {
  if (!props.showCreateCategory || !props.createCategory) return;

  const normalizedCategoryName = normalizeForSearch(categoryName);
  const existingCategory = props.options.find((option) => normalizeForSearch(option.label) === normalizedCategoryName);

  if (existingCategory) {
    selectedCategories.value = [...new Set([...selectedCategories.value, existingCategory.id])];
    resetCategoryCreationForm();
    return;
  }

  const wasCategoryCreated = await props.createCategory(categoryName);

  if (wasCategoryCreated) {
    resetCategoryCreationForm();
  }
}

function handleHideSelectOptions() {
  resetCategoryCreationForm();
  emits("hide");
}

function hideSelectOptions() {
  singleSelect.value?.hide();
  multiSelect.value?.hide();
}
</script>

<template>
  <Select
    ref="singleSelect"
    v-if="props.selectionMode === 'single'"
    v-model="selectedCategory"
    option-label="label"
    option-value="id"
    data-key="id"
    filter
    reset-filter-on-hide
    :options="multiSelectOptions"
    :placeholder="defaultLabel"
    :filter-placeholder="t(k.tools.siteManagement.siteTreeStructure.searchCategory)"
    :empty-filter-message="t(k.global.misc.noResult)"
    :empty-message="t(k.global.misc.noResult)"
    v-bind="attrs"
    :data-test-id="attrs['data-test-id'] ?? DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.trigger"
    :pt="selectPassThroughOptions"
    @hide="handleHideSelectOptions"
    :class="
      cn('site-sections-tree-section-category-filter min-w-0 max-w-full basis-56', {
        '!bg-blue-100/30 !border-blue-200': hasSelectedCategories && filterVariant !== 'inverse-pill',
        'site-sections-tree-section-category-filter--inverse-pill': filterVariant === 'inverse-pill',
      })
    "
  >
    <template #value>
      <SiteSectionsTreeSectionCategoryFilterValue
        :label="singleSelectValueLabel"
        :is-selected="hasSelectedCategories"
        :variant="filterVariant"
      />
    </template>

    <template #option="{ option }: { option: SiteSectionsTreeSectionCategoryFilterOption }">
      <SiteSectionsTreeSectionCategoryFilterOptionItem :option="option" />
    </template>

    <template #dropdownicon>
      <i class="pi pi-chevron-down" />
    </template>

    <template #footer>
      <slot
        name="footer"
        :hide-select-options="hideSelectOptions"
      />
    </template>
  </Select>

  <MultiSelect
    ref="multiSelect"
    v-else
    v-model="selectedCategories"
    option-label="label"
    option-value="id"
    data-key="id"
    filter
    reset-filter-on-hide
    :show-toggle-all="false"
    :options="multiSelectOptions"
    :placeholder="defaultLabel"
    :filter-placeholder="t(k.tools.siteManagement.siteTreeStructure.searchCategory)"
    :empty-filter-message="t(k.global.misc.noResult)"
    :empty-message="t(k.global.misc.noResult)"
    v-bind="attrs"
    :data-test-id="attrs['data-test-id'] ?? DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.trigger"
    :pt="multiSelectPassThroughOptions"
    @hide="handleHideSelectOptions"
    :class="
      cn('site-sections-tree-section-category-filter min-w-0 max-w-full basis-56', {
        '!bg-blue-100/30 !border-blue-200': hasSelectedCategories && filterVariant !== 'inverse-pill',
        'site-sections-tree-section-category-filter--inverse-pill': filterVariant === 'inverse-pill',
      })
    "
  >
    <template #value>
      <SiteSectionsTreeSectionCategoryFilterValue
        :label="defaultLabel"
        :is-selected="hasSelectedCategories"
        :selected-count="selectedCategoryCount"
        :variant="filterVariant"
        show-selected-count
      />
    </template>

    <template #option="{ option }: { option: SiteSectionsTreeSectionCategoryFilterOption }">
      <SiteSectionsTreeSectionCategoryFilterOptionItem :option="option" />
    </template>

    <template #footer>
      <SiteSectionsTreeSectionCategoryFilterFooter
        :is-category-creation-form-visible="isCategoryCreationFormVisible"
        :is-creating-category="props.isCreatingCategory"
        :can-create-category="canCreateCategory"
        :can-clear-category="Boolean(props.clearCategory)"
        :clear-category-label="props.clearCategoryLabel"
        @clear-category="handleClearCategory"
        @confirm-category-creation="confirmCategoryCreation"
        @reset-category-creation-form="resetCategoryCreationForm"
        @start-category-creation="startCategoryCreation"
      />

      <slot
        name="footer"
        :hide-select-options="hideSelectOptions"
      />
    </template>

    <template #dropdownicon>
      <i class="pi pi-chevron-down" />
    </template>
  </MultiSelect>
</template>

<style scoped lang="scss">
:global(.site-sections-tree-section-category-filter-overlay--inverse-pill) {
  inline-size: min(22rem, calc(100vw - 2rem));
}
</style>
